/**
 * Sync retry policy (ADR-0022): exponential backoff with equal jitter for a few fast attempts, then a circuit
 * breaker that pauses automatic attempts for 30 minutes. Pure, so every transition is unit-tested.
 *
 * Sources for the pattern:
 * - AWS Architecture Blog, "Exponential Backoff and Jitter" (Marc Brooker, 2015);
 * - M. Nygard, "Release It!" (circuit breaker: closed → open → half-open);
 * - M. Fowler, "CircuitBreaker" (bliki, 2014).
 *
 *   closed ──fail×N──▶ open ──after openMs──▶ half-open ──ok──▶ closed
 *     ▲  └─fail (<N): back off 2s·2ⁿ, jittered          └─fail──▶ open (another openMs)
 *     └────────────────────────── ok ─────────────────────────────────┘
 * "manual" (Sync now), "online" (connectivity came back) and "start" (app opened) may always probe: they are
 * real signals, not repeats. Writes and timers ("auto") respect the backoff and the open breaker.
 */
export interface RetryPolicy {
  /** Fast attempts before the breaker opens. */
  maxFailures: number;
  baseMs: number;
  capMs: number;
  /** How long the breaker stays open before a probe. */
  openMs: number;
}

export const DEFAULT_RETRY: RetryPolicy = { maxFailures: 5, baseMs: 2000, capMs: 60_000, openMs: 30 * 60_000 };

export type Breaker =
  | { mode: 'closed'; failures: number; nextAt: number }
  | { mode: 'open'; failures: number; nextAt: number; openedAt: number };

export type Trigger = 'auto' | 'manual' | 'online' | 'start';

export const CLOSED: Breaker = { mode: 'closed', failures: 0, nextAt: 0 };

/** Equal jitter: half the exponential delay fixed, half random, so waits stay predictable but never synchronise. */
export function backoffDelay(failures: number, p: RetryPolicy, random: () => number): number {
  const d = Math.min(p.capMs, p.baseMs * 2 ** Math.max(0, failures - 1));
  return Math.round(d / 2 + random() * (d / 2));
}

export function canAttempt(b: Breaker, now: number, trigger: Trigger): boolean {
  if (trigger !== 'auto') return true;
  return now >= b.nextAt;
}

export function onSuccess(): Breaker {
  return CLOSED;
}

export function onFailure(b: Breaker, now: number, p: RetryPolicy = DEFAULT_RETRY, random: () => number = Math.random): Breaker {
  const failures = b.failures + 1;
  if (b.mode === 'open' || failures >= p.maxFailures) return { mode: 'open', failures, nextAt: now + p.openMs, openedAt: b.mode === 'open' ? b.openedAt : now };
  return { mode: 'closed', failures, nextAt: now + backoffDelay(failures, p, random) };
}
